import { formatInstant } from "@/lib/time/counterZone";
/**
 * Builds and saves the recovery-code file.
 *
 * <p>Deliberately a plain .txt rather than a PDF or a CSV: this is a page somebody prints, folds
 * and puts in a drawer, or drops into a password manager. A PDF needs a reader on whatever machine
 * they are locked out on, and a CSV opens in a spreadsheet that helpfully mangles codes into dates.
 *
 * <p>Duplicated per app on purpose -- the consoles are separate Next.js builds with no shared
 * package, and a copied 40-line formatter is cheaper than a package boundary that exists for one
 * function. If a third thing needs it, that is the moment to extract.
 */
export function recoveryCodesFileText(codes: string[], account?: string): string {
  const now = new Date();
  const issued = formatInstant(now, undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  // The origin they are actually using, not a hardcoded production domain -- a file that names a
  // host this person cannot reach is worse than one that names no host at all.
  const console_ = typeof window === "undefined" ? "" : window.location.origin;
  const width = String(codes.length).length;
  const numbered = codes
    .map((code, index) => `    ${String(index + 1).padStart(width, " ")}.  ${code}`)
    .join("\n");

  return `PERKSMITH - TWO-FACTOR RECOVERY CODES
=====================================

Account   ${account || "(this account)"}
Issued    ${issued}
Console   ${console_}

Each code below signs you in ONCE if you lose your phone.
Cross one off as you use it.

${numbered}

KEEP THIS FILE PRIVATE
Anyone holding these codes and your password can sign in as
you. Store it where only you can reach it -- a password
manager, or printed and locked away. Do not email it.

These replace any codes issued to you earlier; older ones no
longer work.

If you run out and no longer have your phone, ask an
administrator to reset two-factor sign-in for your account.

Generated by Perksmith
`;
}

/** Hands the file to the browser. Revokes the object URL so the blob is not held for the session. */
export function downloadRecoveryCodes(codes: string[], account?: string): void {
  // The LOCAL date, not toISOString(): the body prints the issue time in the reader's own zone, and
  // a file called ...-09-07 that says "Issued September 8" inside reads like the wrong file. This is
  // display, and display is the one place the repo's time rule allows a local zone.
  const now = new Date();
  const stamp = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");
  const blob = new Blob([recoveryCodesFileText(codes, account)], {
    type: "text/plain;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `perksmith-recovery-codes-${stamp}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
